import React from 'react';
import {DialogTypes} from 'common/names';
import Dialog from 'components/Dialog';
import Panel from 'components/Panel';
import FormikTextField from 'components/inputs/FormikTextField';
import FormikDropdown from 'components/inputs/FormikDropdown';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import FormikPassphrase from 'components/inputs/FormikPassphrase';
import BitStrengthField from './components/BitStrengthField';
import {KeyPairType, SSHKeyPairTypeCreatable} from 'types/diplomat/enums/KeyPairType';
import {CreateSSHKeyPair, createSSHKeyPairSchema} from 'types/diplomat/keys/CreateSSHKeyPair';
import {INav} from 'features/layout/redux/helpers/FoldersHelper';
import OverwriteConfirmation from 'features/layout/DialogRoutes/dialogs/keys/components/OverwriteConfirmation';
import {useDispatch, useSelector} from 'react-redux';
import selectors from 'features/layout/redux/selectors';
import {useModal} from 'common/hooks/useModal';
import {useTranslation} from 'react-i18next';
import actions from 'redux/keys/actions';
import {folderNames} from 'common/constants';
import PollingStatusText from 'features/layout/DialogRoutes/dialogs/keys/components/PollingStatusText';
import {castToSend} from 'common/yup/castToSend';
import {licenseSelectors} from 'redux/license';
import {Typography} from '@mui/material';
import {AlertDialog} from 'components/dialogs/AlertDialog';
import Formik, {FormOnSubmitHelpers} from 'components/hook-form/Formik';
import {useFormStatusContext} from 'common/context/FormStatusContext';
import {useFieldValue} from 'components/hook-form/useFieldValue';
import {enums} from 'types/enums';

const Form = () => {
   const {t} = useTranslation();
   const [status] = useFormStatusContext();
   const keyType = useFieldValue('keyType');

   return (
      <Panel>
         <FormikTextField autoFocus name="keyName" placeholder={t('Key Name')} />
         <FormikDropdown
            name="keyType"
            placeholder={t('Key Type')}
            options={SSHKeyPairTypeCreatable.options}
         />
         {keyType === KeyPairType.enum.ECDSA ? (
            <FormikDropdown
               name="eccurveType"
               label={t('Elliptic Curve')}
               options={enums.eccurveTypeCreatable.options}
            />
         ) : (
            <BitStrengthField />
         )}
         <FormikPassphrase name="passPhrase" placeholder={t('Passphrase')} />
         <FormikPassphrase name="existingPassphrase" placeholder={t('Repeat Passphrase')} />
         <PollingStatusText pollCount={status.pollCount} />
      </Panel>
   );
};

type CreateSSHKeyDialogProps = {
   data?: INav;
   onClose: () => void;
};
const CreateSSHKeyDialog = ({data, onClose}: CreateSSHKeyDialogProps) => {
   const {t} = useTranslation();
   const dispatch = useDispatch();
   const license = useSelector(licenseSelectors.getLicense);
   const keys = useSelector(selectors.clientSshKeys);
   const {ssh} = useSelector(selectors.keysCountSelector);
   const defaultFolder = useSelector(selectors.folderByName(folderNames.SSH_KEY_PAIRS));
   const {
      open: isOverwriteConfirmOpened,
      handleOpen: openOverwriteConfirm,
      handleClose: closeOverwriteConfirm,
   } = useModal();

   const [submitProps, setSubmitProps] = React.useState<
      [CreateSSHKeyPair, FormOnSubmitHelpers<CreateSSHKeyPair>]
   >();
   const handleSubmit = (
      values: CreateSSHKeyPair,
      helpers: FormOnSubmitHelpers<CreateSSHKeyPair>
   ) => {
      if (!values.overwrite && keys.some((key) => key.name === values.keyName)) {
         setSubmitProps([values, helpers]);
         openOverwriteConfirm();
         return;
      }
      dispatch(actions.createSSHKeyPair(castToSend(createSSHKeyPairSchema, values), helpers));
   };
   if (license.numKeys === ssh) {
      return (
         <AlertDialog
            title={t('Error Creating SSH Key Pair')}
            onClose={onClose}
            content={<Typography>{t('Maximum number of SSH keys has been reached')}</Typography>}
         />
      );
   }
   return (
      <Dialog maxWidth="sm" header={DialogTypes.CreateSSHKeyPair.dialogName} close={onClose}>
         <Formik
            enableReinitialize
            initialStatus={{pollCount: 0}}
            initialValues={createSSHKeyPairSchema.cast({
               folderId: data?.id || defaultFolder?.id,
            })}
            validationSchema={createSSHKeyPairSchema}
            onSubmit={handleSubmit}
         >
            <>
               <OverwriteConfirmation
                  open={isOverwriteConfirmOpened}
                  keyType={t('an SSH key')}
                  keyName={submitProps?.[0].keyName}
                  onOk={() => {
                     handleSubmit({...submitProps![0], overwrite: true}, submitProps![1]);
                     closeOverwriteConfirm();
                  }}
                  onCancel={closeOverwriteConfirm}
               />
               <Form />
               <FormFooterButtons cancel={onClose} />
            </>
         </Formik>
      </Dialog>
   );
};

export default CreateSSHKeyDialog;
